import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, ScanSearch } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { VerifyChip } from "../components/DataLabel";

const ksh = (n) => (n ? `KSh ${Math.round(n).toLocaleString()}` : "—");
const EMPTY = { county: "", ward: "", location: "peri_urban", acres: "", kind: "Asking price", amount_shillings: "",
  dated: "", description: "", source_name: "", source_url: "", credit_visibility: "anonymous", credit_name: "",
  contact_email: "", consent_correction: false };

/** Real Kenyan prices against our own estimate, and an open door for anyone who knows a better number. */
export default function LandComparables() {
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [zones, setZones] = useState(null);
  const [county, setCounty] = useState(params.get("county") || "");
  const [counties, setCounties] = useState([]);
  const [form, setForm] = useState({ ...EMPTY, county: params.get("county") || "" });
  const [state, setState] = useState({ status: "idle" });

  useSeo({
    title: "Land Comparables — Check Our Numbers Against Real Deals — KickStartKenyaFootball",
    description:
      "Every real Kenyan land price we can source — sales, asking prices, leases and ground rents — with our own estimate beside it and the variance stated, including where we are wrong.",
  });

  const load = (c) => api.get(`/comparables${c ? `?county=${encodeURIComponent(c)}` : ""}`)
    .then((r) => setD(r.data)).catch(() => setD(null));

  useEffect(() => { load(county); }, [county]);
  useEffect(() => { api.get("/land/value-zones").then((r) => setZones(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/counties").then((r) => {
      const rows = Array.isArray(r.data) ? r.data : r.data.counties || [];
      setCounties(rows.map((x) => x.county || x.name).filter(Boolean));
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "sending" });
    try {
      const r = await api.post("/comparables", {
        ...form,
        acres: parseFloat(form.acres || "0") || 0,
        amount_shillings: parseFloat(String(form.amount_shillings).replace(/[^\d.]/g, "")) || 0,
      });
      setState({ status: "done", reference: r.data.reference, next: r.data.next, sourced: r.data.sourced });
      setForm({ ...EMPTY, county: form.county });
      load(county);
    } catch (err) {
      setState({ status: "error", message: err?.response?.data?.detail || "" });
    }
  };

  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="comparables-loading" />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const field = "mt-2 w-full border border-white/20 bg-transparent px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none";
  const label = "mono block text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400";

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="comparables-page">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <p className="overline text-ochre">Evidence log</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d.heading}</h1>
          <p className="body-copy mt-6 max-w-3xl">{d.intro}</p>
          <div className="mono mt-8 flex max-w-3xl items-start gap-3 border-l-2 border-maasai bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400">
            <ScanSearch size={16} className="mt-0.5 shrink-0 text-maasai" />
            <span><VerifyChip v="estimate" /> {d.caveat}</span>
          </div>
        </Reveal>

        <section className="mt-12" data-testid="comparables-score">
          <div className="grid gap-px bg-zinc-800 sm:grid-cols-3">
            {[["Comparables published", d.published],
              ["Average gap to our estimate", d.mean_variance_pct === null ? "—" : `${d.mean_variance_pct}%`],
              [`Within ±${d.band_pct}%`, d.within_band_pct === null ? "—" : `${d.within_band_pct}%`]]
              .map(([k, v], i) => (
                <div key={k} className="bg-[#0b0b0b] p-7" data-testid={`comparables-stat-${i}`}>
                  <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">{k}</p>
                  <p className="mono mt-3 text-lg text-white">{v}</p>
                </div>
              ))}
          </div>
        </section>

        {zones ? (
          <section className="mt-16" data-testid="value-zones">
            <p className="eyebrow">Whose number wins</p>
            <h2 className="display-3 mt-4">{zones.heading}</h2>
            <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">{zones.intro}</p>
            <div className="mt-8 grid gap-px bg-zinc-800 sm:grid-cols-3">
              {[["Counties in the Ministry's programme", zones.counties_in_programme],
                ["Counties where its number replaces ours", zones.counties_deferred_to],
                ["Value zone maps published", zones.counts.published]].map(([k, v], i) => (
                  <div key={k} className="bg-[#0b0b0b] p-7" data-testid={`value-zones-stat-${i}`}>
                    <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">{k}</p>
                    <p className="mono mt-3 text-lg text-white">{v}</p>
                  </div>
                ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="value-zones-rows">
              {zones.rows.map((r) => (
                <Link key={r.county} to={`/land-value/${r.county.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`mono border px-3 py-2 text-[0.6rem] uppercase tracking-[0.14em] transition-colors ${
                    r.defers ? "border-ochre/50 text-ochre hover:bg-ochre hover:text-black"
                      : "border-zinc-800 text-zinc-400 hover:border-zinc-500 hover:text-white"}`}
                  data-testid={`value-zone-${r.county.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {r.county} · {r.stage_label.replace("Ministry ", "")}
                </Link>
              ))}
            </div>
            <p className="mt-6 max-w-3xl text-xs leading-relaxed text-zinc-500">
              {zones.programme_note} {zones.caveat}
            </p>
            <a href={zones.source.url} target="_blank" rel="noreferrer"
              className="mono mt-4 inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.16em] text-ochre hover:text-white"
              data-testid="value-zones-source">
              {zones.source.name} <ArrowUpRight size={11} />
            </a>
          </section>
        ) : null}

        <section className="mt-16" data-testid="comparables-log">          {d.what_the_log_shows ? (
            <p className="mb-10 max-w-3xl border-l-2 border-ochre bg-[#0b0b0b] p-5 text-sm leading-relaxed text-zinc-300"
              data-testid="comparables-pattern">
              {d.what_the_log_shows}
            </p>
          ) : null}
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow">The log</p>
              <h2 className="display-3 mt-4">Every Price We Can Source</h2>
            </div>
            <label className="block">
              <span className={label}>Filter by county</span>
              <select value={county} onChange={(e) => setCounty(e.target.value)} data-testid="comparables-county-filter"
                className="mt-2 border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none">
                <option value="">Every county</option>
                {counties.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>

          {d.rows.length ? (
            <div className="mt-8 overflow-x-auto border border-zinc-900">
              <table className="w-full min-w-[900px] text-left" data-testid="comparables-table">
                <thead>
                  <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                    {["What it was", "County", "Size", "The real price", "Our estimate", "Gap", "Source"].map((h) => (
                      <th key={h} className="mono px-4 py-3 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.rows.map((r) => (
                    <tr key={r.reference} className="border-b border-zinc-900 align-top" data-testid={`comparable-${r.reference}`}>
                      <td className="px-4 py-4">
                        <span className="block font-display text-sm uppercase text-white">{r.kind}</span>
                        <span className="mt-1 block text-xs leading-relaxed text-zinc-500">{r.description}</span>
                        {r.dated ? <span className="mono mt-1 block text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">{r.dated}</span> : null}
                      </td>
                      <td className="mono px-4 py-4 text-xs text-zinc-300">{r.county}</td>
                      <td className="mono px-4 py-4 text-xs text-zinc-300">{r.acres} acres</td>
                      <td className="mono px-4 py-4 text-xs text-white">{ksh(r.amount_shillings)}</td>
                      <td className="mono px-4 py-4 text-xs text-zinc-300">{ksh(r.ours_shillings)}</td>
                      <td className={`mono px-4 py-4 text-sm ${Math.abs(r.variance_pct) <= d.band_pct ? "text-ochre" : "text-maasai"}`}>
                        {r.variance_pct > 0 ? "+" : ""}{r.variance_pct}%
                        <span className="mono mt-1 block text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">
                          we are {r.we_are}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {r.source_url ? (
                          <a href={r.source_url} target="_blank" rel="noreferrer"
                            className="mono inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.16em] text-ochre hover:text-white">
                            {r.source_name || "Source"} <ArrowUpRight size={11} />
                          </a>
                        ) : (
                          <span className="mono text-[0.6rem] uppercase tracking-[0.16em] text-zinc-500">{r.source_name || "—"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mono mt-8 max-w-2xl border-l-2 border-ochre bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-ochre"
              data-testid="comparables-empty">
              Nothing published yet. The first comparable in the log is the one somebody sends below.
            </p>
          )}
        </section>

        <section className="mt-16" data-testid="comparables-coverage">
          <p className="eyebrow">Where the evidence is thin</p>
          <h2 className="display-3 mt-4">Sourced Prices By County</h2>
          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">
            {d.counties_with_none} of the 47 counties have no sourced price in this log at all, so our figure
            there stands unchallenged. One price from one person changes that.
          </p>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="coverage-rows">
            {d.coverage.map((r) => (
              <button key={r.county} type="button" onClick={() => { setCounty(r.county); setForm((f) => ({ ...f, county: r.county })); }}
                data-testid={`coverage-${r.county.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`mono border px-3 py-2 text-[0.6rem] uppercase tracking-[0.14em] transition-colors ${
                  r.count ? "border-ochre/50 text-ochre hover:bg-ochre hover:text-black"
                    : "border-zinc-800 text-zinc-500 hover:border-maasai hover:text-white"}`}>
                {r.county} · {r.count || "none"}
              </button>
            ))}
          </div>
        </section>

        <section className="mt-16" data-testid="comparables-submit">
          <p className="eyebrow">{d.correction.eyebrow}</p>
          <h2 className="display-3 mt-4">{d.correction.heading}</h2>
          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-300">{d.correction.body}</p>
          <p className="mt-4 max-w-3xl border-l-2 border-maasai bg-[#0b0b0b] p-5 text-xs leading-relaxed text-zinc-300"
            data-testid="comparables-guardrail">
            {d.correction.guardrail}
          </p>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-400">{d.how_to_help}</p>
          {state.status === "done" ? (
            <div className="mt-8 max-w-3xl border border-ochre/40 bg-[#0b0b0b] p-7" data-testid="comparables-done">
              <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-ochre">Received</p>
              <p className="mt-3 font-display text-2xl uppercase tracking-tight text-white">{state.reference}</p>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">{state.next}</p>
              <button type="button" onClick={() => setState({ status: "idle" })} data-testid="comparable-send-another"
                className="mono mt-5 text-[0.6rem] uppercase tracking-[0.16em] text-ochre hover:text-white">
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 grid max-w-4xl gap-5 bg-[#0b0b0b] p-7 md:grid-cols-2"
              data-testid="comparables-form">
              <label className="block">
                <span className={label}>County</span>
                <select value={form.county} onChange={set("county")} required data-testid="comparable-county" className={`${field} bg-black`}>
                  <option value="">Choose a county</option>
                  {counties.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block">
                <span className={label}>What kind of price</span>
                <select value={form.kind} onChange={set("kind")} data-testid="comparable-kind" className={`${field} bg-black`}>
                  {d.kinds.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </label>
              <label className="block">
                <span className={label}>Size in acres</span>
                <input value={form.acres} onChange={set("acres")} inputMode="decimal" required data-testid="comparable-acres" className={field} />
              </label>
              <label className="block">
                <span className={label}>The amount in shillings</span>
                <input value={form.amount_shillings} onChange={set("amount_shillings")} inputMode="numeric" required data-testid="comparable-amount" className={field} />
              </label>
              <label className="block">
                <span className={label}>Ward or nearest town</span>
                <input value={form.ward} onChange={set("ward")} placeholder="No plot or LR number" data-testid="comparable-ward" className={field} />
              </label>
              <label className="block">
                <span className={label}>When</span>
                <input value={form.dated} onChange={set("dated")} placeholder="e.g. March 2026" data-testid="comparable-dated" className={field} />
              </label>
              <label className="block">
                <span className={label}>Credit for this figure</span>
                <select value={form.credit_visibility} onChange={set("credit_visibility")} data-testid="comparable-credit-visibility" className={`${field} bg-black`}>
                  <option value="anonymous">Publish it anonymously</option>
                  <option value="name">Credit me or my organisation</option>
                </select>
              </label>
              {form.credit_visibility === "name" ? (
                <label className="block">
                  <span className={label}>Name to credit</span>
                  <input value={form.credit_name} onChange={set("credit_name")} data-testid="comparable-credit-name" className={field} />
                </label>
              ) : null}
              <label className="block">
                <span className={label}>Your email, if we may ask about the source</span>
                <input value={form.contact_email} onChange={set("contact_email")} type="email" data-testid="comparable-contact-email" className={field} />
              </label>
              <label className="block md:col-span-2">
                <span className={label}>What happened, in a line or two</span>
                <textarea value={form.description} onChange={set("description")} rows={2} data-testid="comparable-description" className={field} />
              </label>
              <label className="block">
                <span className={label}>Source name — required before we can publish it</span>
                <input value={form.source_name} onChange={set("source_name")} placeholder="Valuer's report, tender notice, listing, newspaper" data-testid="comparable-source-name" className={field} />
              </label>
              <label className="block">
                <span className={label}>Link to the evidence</span>
                <input value={form.source_url} onChange={set("source_url")} data-testid="comparable-source-url" className={field} />
              </label>
              <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-zinc-300 md:col-span-2"
                data-testid="comparable-consent-label">
                <input type="checkbox" checked={form.consent_correction} required data-testid="comparable-consent"
                  onChange={(e) => setForm({ ...form, consent_correction: e.target.checked })}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-maasai" />
                <span>
                  I understand this is a correction to published research. Nothing is paid or owed for it,
                  KickStartKenyaFootball makes no introductions and takes no part in any transaction, this does
                  not offer any land for sale or lease, and I am not sending anybody&rsquo;s title, identity
                  documents or contact details.
                </span>
              </label>
              <div className="md:col-span-2 flex flex-wrap items-center gap-4">
                <button type="submit" disabled={state.status === "sending"} data-testid="comparable-submit"
                  className="mono border border-ochre px-6 py-3 text-[0.6rem] uppercase tracking-[0.16em] text-ochre transition-colors hover:bg-ochre hover:text-black disabled:opacity-50">
                  {state.status === "sending" ? "Sending…" : "Correct our number"}
                </button>
                {state.status === "error" ? (
                  <span className="max-w-md text-xs leading-relaxed text-maasai" data-testid="comparable-error">
                    {state.message || "That did not send. Try again, or email media@kickstartkenyafootball.com."}
                  </span>
                ) : null}
              </div>
            </form>
          )}
          <div className="mono mt-8 flex flex-wrap gap-4 text-[0.6rem] uppercase tracking-[0.16em]">
            <Link to="/lease-comparison" className="text-ochre hover:text-white" data-testid="comparables-to-lease">Price every route</Link>
            <Link to="/site-appraisal" className="text-ochre hover:text-white" data-testid="comparables-to-appraisal">Appraise a plot</Link>
            <Link to="/sources" className="text-ochre hover:text-white" data-testid="comparables-to-sources">Sources and method</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
